// `me.notification.new` and `alerts.new` (spec/09 §6, spec/16 §2, §6; F-NTF-03).
//
// The pipeline's instant in-app delivery. When a notification is written to a person's inbox, the
// server publishes one of these so an open app or console updates without polling:
//
//   me.notification.new   on `player:{id}` or `member:{id}` — the recipient's own channel. Every
//                         notification the person receives, so the bell lights up the moment it lands.
//   alerts.new            on `venue:{id}:floor` — a staff floor alert (a call for help, a station
//                         offline) that the whole on-duty floor watches, not one member (spec/16 §4.3
//                         N-17/N-18).
//
// Both carry the id and enough to render a toast without a refetch; the durable copy is the inbox row
// (`GET …/notifications`). The gateway that delivers these to web/app clients is F-RT (#379).
import { z } from "../zod.js";
import { StationAlertSchema } from "../routes/station-health.js";
import { ALERTS_CLEARED, ALERTS_NEW } from "./constants.js";
import { realtimeMessage } from "./envelope.js";
/** What a new-notification frame carries: enough to show a toast and deep-link, not the whole row. */
export const NotificationNewPayloadSchema = z
    .object({
    /** The inbox row's id (`ntf_…`), so a client can mark it read or open it. */
    id: z.string(),
    /** The catalogue id (N-01…N-29). */
    code: z.string(),
    category: z.string(),
    title: z.string(),
    body: z.string(),
    /** The venue it is about, when it is about one; else absent. */
    venueId: z.string().nullable(),
    /** Whether it is urgent (session ending, security) and ignored quiet hours (spec/16 §3). */
    urgent: z.boolean(),
})
    .openapi('NotificationNewPayload', { description: 'A notification that just landed in the inbox.' });
/** `evt` on `player:{id}` / `member:{id}` — the recipient's own channel. */
export const ME_NOTIFICATION_NEW = 'me.notification.new';
export const MeNotificationNewMessage = realtimeMessage('evt', ME_NOTIFICATION_NEW, NotificationNewPayloadSchema, 'MeNotificationNewMessage');
/** `evt` on `venue:{id}:floor` — a staff floor alert the on-duty floor watches (spec/16 §4.3). */
export { ALERTS_NEW };
export const AlertsNewMessage = realtimeMessage('evt', ALERTS_NEW, NotificationNewPayloadSchema, 'AlertsNewMessage');
/**
 * `evt` on `venue:{id}:floor` — a station alert cleared (F-STN-11): the condition is gone, so the tile's badge and
 * the "Needs attention" row go without a manual dismiss. The open side is `alerts.new` (N-18). Outside the floor's
 * version sequence, so a console refetches its snapshot on it (spec/09 §4).
 */
export { ALERTS_CLEARED };
export const AlertsClearedPayloadSchema = z
    .object({ alert: StationAlertSchema })
    .openapi('AlertsClearedPayload', {
    description: 'A station alert left the floor (F-STN-11): its condition went, it expired after a quiet period, or staff cleared or snoozed it (2026-10-02).',
});
export const AlertsClearedMessage = realtimeMessage('evt', ALERTS_CLEARED, AlertsClearedPayloadSchema, 'AlertsClearedMessage');
//# sourceMappingURL=notifications.js.map